import { effectiveRouteLegs, localDateString, sortStops, summarizeDay, summarizeTrip } from "./domain";
import { routeLegNavigationUrl } from "./routeService";
import { nominatimPlaceSearchProvider, type PlaceSearchProvider } from "./search";
import type {
  AddStopInput,
  AppState,
  CreateTripInput,
  ItineraryStop,
  PlaceCandidate,
  TransportMode,
  TravelPace,
  Trip,
  UpdateStopInput,
  RouteRefreshOptions,
  RouteRefreshResult,
} from "./types";
import type {
  AgentUndoAvailability,
  AgentUndoResult,
  TripPatchPreview,
} from "./useRoam";

export type WebMCPStatus = "checking" | "ready" | "unavailable" | "error";

export interface WebMCPController {
  getState: () => AppState;
  createTrip: (input: CreateTripInput, source: "agent") => Trip;
  updateTrip: (tripId: string, input: Partial<CreateTripInput>, source: "agent") => Trip;
  openTrip: (tripId: string, source: "agent") => Trip;
  addStop: (tripId: string, input: AddStopInput, source: "agent") => ItineraryStop;
  updateStop: (tripId: string, stopId: string, input: UpdateStopInput, source: "agent") => ItineraryStop;
  moveStop: (tripId: string, stopId: string, targetDayId: string | null, targetPosition: number, source: "agent") => void;
  proposeRelaxedDay: (tripId: string, dayId: string) => TripPatchPreview;
  getAgentUndoAvailability: () => AgentUndoAvailability;
  undoLastAgentChange: () => AgentUndoResult;
  refreshRoutes: (tripId: string, options?: RouteRefreshOptions) => Promise<RouteRefreshResult>;
}

export interface WebMCPBindings {
  getController: () => WebMCPController;
  setStatus: (status: WebMCPStatus) => void;
  setMessage: (message: string) => void;
}

export interface WebMCPDependencies {
  placeSearchProvider?: PlaceSearchProvider;
  now?: () => Date;
  registrationSignal?: AbortSignal;
}

export type WebMCPErrorCode =
  | "INVALID_INPUT"
  | "TRIP_NOT_FOUND"
  | "DAY_NOT_FOUND"
  | "STOP_NOT_FOUND"
  | "SEARCH_RESULT_REQUIRED"
  | "SEARCH_UNAVAILABLE"
  | "DATE_RANGE_CONFLICT"
  | "LOCKED_ITEM"
  | "AGENT_UNDO_BLOCKED"
  | "NO_UNDO_AVAILABLE"
  | "ROUTING_CONSENT_REQUIRED"
  | "UNEXPECTED_ERROR";

export interface WebMCPError {
  code: WebMCPErrorCode;
  message: string;
  retryable: boolean;
  needsUserInput: boolean;
  suggestedTool?: string;
  details?: Record<string, unknown>;
}

export type WebMCPResult<T> =
  | { ok: true; data: T; meta?: Record<string, unknown> }
  | { ok: false; error: WebMCPError };

type ToolInput = Record<string, unknown>;
interface ToolExecution<T> { data: T; meta?: Record<string, unknown> }
interface CursorPayload { version: 1; offset: number; signature: string }

const PLACE_CACHE_LIMIT = 50;
const TRIP_PAGE_LIMIT = 20;
const STOP_PAGE_LIMIT = 25;
export const WEBMCP_TOOL_COUNT = 12;

const travelCapabilities = {
  placeSearch: "OpenStreetMap Nominatim",
  itineraryEditing: true,
  undatedTrips: true,
  routePlanning: "road_network_when_available",
  routeDistance: "road_network_when_available",
  routeFreshness: true,
  transitRouting: "handoff_with_manual_buffer",
  openingHours: false,
  liveRouting: false,
  liveNavigation: false,
  liveTransit: false,
  liveTraffic: false,
  weather: false,
  bookings: false,
} as const;

class ToolFailure extends Error {
  constructor(readonly failure: WebMCPError) {
    super(failure.message);
    this.name = "ToolFailure";
  }
}

function fail(code: WebMCPErrorCode, message: string, options: Partial<Omit<WebMCPError, "code" | "message">> = {}): never {
  throw new ToolFailure({ code, message, retryable: options.retryable ?? false, needsUserInput: options.needsUserInput ?? false, suggestedTool: options.suggestedTool, details: options.details });
}

function normalizeFailure(error: unknown): WebMCPError {
  if (error instanceof ToolFailure) return error.failure;
  const message = error instanceof Error ? error.message : "The tool could not complete this request.";
  if (error && typeof error === "object" && "code" in error && error.code === "ROUTING_CONSENT_REQUIRED") {
    return { code: "ROUTING_CONSENT_REQUIRED", message, retryable: false, needsUserInput: true };
  }
  if (/lock|reservation/i.test(message)) return { code: "LOCKED_ITEM", message, retryable: false, needsUserInput: true };
  if (/shorten|removed days|date range/i.test(message)) return { code: "DATE_RANGE_CONFLICT", message, retryable: false, needsUserInput: true };
  return { code: "UNEXPECTED_ERROR", message, retryable: false, needsUserInput: false };
}

async function executeTool<T>(bindings: WebMCPBindings, action: string, execute: () => ToolExecution<T> | Promise<ToolExecution<T>>): Promise<WebMCPResult<T>> {
  try {
    const result = await execute();
    return result.meta ? { ok: true, data: result.data, meta: result.meta } : { ok: true, data: result.data };
  } catch (error) {
    if (error instanceof Error && error.name === "AbortError") throw error;
    const failure = normalizeFailure(error);
    bindings.setMessage(`Agent could not ${action}: ${failure.message}`);
    return { ok: false, error: failure };
  }
}

function cleanString(value: unknown, label: string, required = true): string {
  if (typeof value !== "string") {
    if (!required && value === undefined) return "";
    fail("INVALID_INPUT", `${label} must be text.`, { needsUserInput: true });
  }
  const clean = value.trim();
  if (required && !clean) fail("INVALID_INPUT", `${label} is required.`, { needsUserInput: true });
  return clean;
}

function cleanNumber(value: unknown, label: string): number {
  if (typeof value !== "number" || !Number.isFinite(value)) fail("INVALID_INPUT", `${label} must be a number.`, { needsUserInput: true });
  return value;
}

function cleanInteger(value: unknown, label: string, minimum: number, maximum: number): number {
  const clean = cleanNumber(value, label);
  if (!Number.isInteger(clean) || clean < minimum || clean > maximum) fail("INVALID_INPUT", `${label} must be an integer between ${minimum} and ${maximum}.`, { needsUserInput: true });
  return clean;
}

function cleanPace(value: unknown, required = false): TravelPace | undefined {
  if (value === undefined && !required) return undefined;
  if (value !== "relaxed" && value !== "balanced" && value !== "packed") fail("INVALID_INPUT", "Pace must be relaxed, balanced, or packed.", { needsUserInput: true });
  return value;
}

function cleanTransport(value: unknown): TransportMode | undefined {
  if (value === undefined) return undefined;
  if (value !== "walk" && value !== "transit" && value !== "drive" && value !== "bike" && value !== "other") fail("INVALID_INPUT", "Transport must be walk, transit, drive, bike, or other.", { needsUserInput: true });
  return value;
}

function hashSignature(value: string): string {
  let hash = 2166136261;
  for (let index = 0; index < value.length; index += 1) {
    hash ^= value.charCodeAt(index);
    hash = Math.imul(hash, 16777619);
  }
  return (hash >>> 0).toString(36);
}

function encodeCursor(offset: number, signature: string): string {
  return btoa(JSON.stringify({ version: 1, offset, signature } satisfies CursorPayload));
}

function decodeCursor(value: unknown, signature: string): number {
  if (typeof value !== "string" || !value) fail("INVALID_INPUT", "Cursor must be the nextCursor returned by the previous page.");
  try {
    const payload = JSON.parse(atob(value)) as Partial<CursorPayload>;
    if (payload.version !== 1 || !Number.isSafeInteger(payload.offset) || (payload.offset ?? -1) < 0 || payload.signature !== signature) fail("INVALID_INPUT", "Cursor is malformed or stale. Request the first page again.");
    return payload.offset as number;
  } catch (error) {
    if (error instanceof ToolFailure) throw error;
    fail("INVALID_INPUT", "Cursor is malformed or stale. Request the first page again.");
  }
}

function cleanPage(input: ToolInput, maximum: number, defaultLimit: number, signature: string) {
  const limit = input.limit === undefined ? defaultLimit : cleanInteger(input.limit, "Limit", 1, maximum);
  const offset = input.cursor === undefined ? 0 : decodeCursor(input.cursor, signature);
  return { offset, limit };
}

function pageItems<T>(items: T[], offset: number, limit: number, signature: string) {
  const page = items.slice(offset, offset + limit);
  const nextOffset = offset + page.length;
  return { items: page, total: items.length, nextCursor: nextOffset < items.length ? encodeCursor(nextOffset, signature) : null };
}

function resolveTrip(controller: WebMCPController, rawTripId: unknown): Trip {
  const state = controller.getState();
  const tripId = typeof rawTripId === "string" && rawTripId.trim() ? rawTripId.trim() : state.activeTripId;
  const trip = state.trips.find((candidate) => candidate.id === tripId);
  if (!trip) fail("TRIP_NOT_FOUND", "Trip not found. Use list_trips to choose a trip first.", { needsUserInput: true, suggestedTool: "list_trips" });
  return trip;
}

function resolveDay(trip: Trip, rawDayId: unknown) {
  const dayId = cleanString(rawDayId, "Day ID");
  const day = trip.days.find((candidate) => candidate.id === dayId);
  if (!day) fail("DAY_NOT_FOUND", "Trip day not found. Use summarize_trip to choose a dayId first.", { needsUserInput: true, suggestedTool: "summarize_trip" });
  return day;
}

function resolveStop(trip: Trip, rawStopId: unknown): ItineraryStop {
  const stopId = cleanString(rawStopId, "Stop ID");
  const stop = trip.stops.find((candidate) => candidate.id === stopId);
  if (!stop) fail("STOP_NOT_FOUND", "Stop not found. Use summarize_trip to choose a stop first.", { needsUserInput: true, suggestedTool: "summarize_trip" });
  return stop;
}

function sharedTripIdProperty() {
  return { type: "string", description: "Trip ID from list_trips. Omit to use the trip currently open in Roam." };
}

function tripData(trip: Trip) {
  return { id: trip.id, name: trip.name, destination: trip.destination, durationDays: trip.durationDays, startDate: trip.startDate ?? null, endDate: trip.endDate ?? null, dated: Boolean(trip.startDate && trip.endDate), days: trip.days.map((day) => ({ id: day.id, dayIndex: day.dayIndex, date: day.date ?? null, locked: day.locked })), pace: trip.pace, notes: trip.notes, stopCount: trip.stops.length, createdAt: trip.createdAt, updatedAt: trip.updatedAt };
}

function stopData(stop: ItineraryStop, currency: string) {
  return { id: stop.id, dayId: stop.dayId, order: stop.order, name: stop.name, address: stop.address, lat: stop.lat, lon: stop.lon, date: stop.date ?? null, startTime: stop.startTime || null, durationMinutes: stop.durationMinutes, notes: stop.notes, cost: stop.cost, currency, transport: stop.transport, travelBufferMinutes: stop.travelBufferMinutes ?? null, category: stop.category, placeRef: stop.placeRef ?? null, locks: stop.locks, explanation: stop.explanation ?? null, createdAt: stop.createdAt, updatedAt: stop.updatedAt };
}

function runDomain<T>(execute: () => T): T {
  try { return execute(); }
  catch (error) {
    const message = error instanceof Error ? error.message : "The input is invalid.";
    if (/lock|reservation/i.test(message)) fail("LOCKED_ITEM", message, { needsUserInput: true });
    if (/shorten|removed days|date range/i.test(message)) fail("DATE_RANGE_CONFLICT", message, { needsUserInput: true, suggestedTool: "move_stop" });
    fail("INVALID_INPUT", message, { needsUserInput: true });
  }
}

export function buildWebMCPTools(bindings: WebMCPBindings, dependencies: WebMCPDependencies = {}): WebMCP.ModelContextTool[] {
  const controller = () => bindings.getController();
  const placeSearchProvider = dependencies.placeSearchProvider ?? nominatimPlaceSearchProvider;
  const now = dependencies.now ?? (() => new Date());
  const verifiedPlaces = new Map<string, PlaceCandidate>();
  dependencies.registrationSignal?.addEventListener("abort", () => verifiedPlaces.clear(), { once: true });

  function cachePlace(place: PlaceCandidate): string {
    const placeRef = `osm:nominatim:${place.id}`;
    verifiedPlaces.delete(placeRef);
    verifiedPlaces.set(placeRef, { ...place });
    while (verifiedPlaces.size > PLACE_CACHE_LIMIT) {
      const oldest = verifiedPlaces.keys().next().value;
      if (typeof oldest !== "string") break;
      verifiedPlaces.delete(oldest);
    }
    return placeRef;
  }

  function readCachedPlace(placeRef: string): PlaceCandidate | undefined {
    const place = verifiedPlaces.get(placeRef);
    if (!place) return undefined;
    verifiedPlaces.delete(placeRef);
    verifiedPlaces.set(placeRef, place);
    return { ...place };
  }

  function isValidPlace(place: PlaceCandidate): boolean {
    return Boolean(place.id && place.name.trim() && place.displayName.trim() && Number.isFinite(place.lat) && place.lat >= -90 && place.lat <= 90 && Number.isFinite(place.lon) && place.lon >= -180 && place.lon <= 180);
  }

  return [
    {
      name: "list_trips",
      title: "List trips",
      description: "Read local trips and profile defaults. Undated trips are planning work, not past trips.",
      inputSchema: { type: "object", properties: { scope: { type: "string", enum: ["all", "upcoming", "past"] }, limit: { type: "integer", minimum: 1, maximum: TRIP_PAGE_LIMIT }, cursor: { type: "string" } }, additionalProperties: false },
      annotations: { readOnlyHint: true, untrustedContentHint: true },
      execute: async (input: ToolInput) => executeTool(bindings, "list trips", () => {
        const state = controller().getState();
        const scope = input.scope ?? "all";
        if (scope !== "all" && scope !== "upcoming" && scope !== "past") fail("INVALID_INPUT", "Scope must be all, upcoming, or past.");
        const today = localDateString(now());
        const filtered = state.trips.filter((trip) => scope === "past" ? Boolean(trip.endDate && trip.endDate < today) : scope === "upcoming" ? !trip.endDate || trip.endDate >= today : true).sort((left, right) => right.updatedAt.localeCompare(left.updatedAt));
        const signature = hashSignature(`${scope}:${filtered.map((trip) => `${trip.id}:${trip.updatedAt}`).join("|")}`);
        const { offset, limit } = cleanPage(input, TRIP_PAGE_LIMIT, 10, signature);
        const page = pageItems(filtered, offset, limit, signature);
        bindings.setMessage(`Agent read ${page.items.length} of ${page.total} matching trips.`);
        return { data: { profile: { homeCity: state.profile.homeCity, currency: state.profile.currency, defaultPace: state.profile.pace, onlineRoutingEnabled: state.profile.onlineRoutingEnabled === true }, activeTripId: state.activeTripId, trips: page.items.map((trip) => ({ ...tripData(trip), needsPlanning: trip.stops.length === 0 || trip.stops.some((stop) => !stop.startTime) })), total: page.total, nextCursor: page.nextCursor }, meta: { capabilities: travelCapabilities } };
      }),
    },
    {
      name: "create_trip",
      title: "Create a trip",
      description: "Create a dated or undated trip. When the user gives a duration but no exact dates, pass durationDays and do not invent dates. Omit pace to use the profile default.",
      inputSchema: { type: "object", properties: { name: { type: "string" }, destination: { type: "string" }, durationDays: { type: "integer", minimum: 1, maximum: 30 }, startDate: { type: "string", description: "Optional exact YYYY-MM-DD; provide endDate too." }, endDate: { type: "string", description: "Optional exact YYYY-MM-DD; provide startDate too." }, pace: { type: "string", enum: ["relaxed", "balanced", "packed"] }, notes: { type: "string" } }, required: ["name", "destination"], additionalProperties: false },
      annotations: { readOnlyHint: false, untrustedContentHint: true },
      execute: async (input: ToolInput) => executeTool(bindings, "create a trip", () => {
        const hasStart = Object.hasOwn(input, "startDate");
        const hasEnd = Object.hasOwn(input, "endDate");
        if (hasStart !== hasEnd) fail("INVALID_INPUT", "Provide both startDate and endDate, or neither.", { needsUserInput: true });
        if (!hasStart && input.durationDays === undefined) fail("INVALID_INPUT", "Provide durationDays when exact dates are unknown. Do not invent dates.", { needsUserInput: true });
        const trip = runDomain(() => controller().createTrip({ name: cleanString(input.name, "Name"), destination: cleanString(input.destination, "Destination"), ...(hasStart ? { startDate: cleanString(input.startDate, "Start date"), endDate: cleanString(input.endDate, "End date") } : { durationDays: cleanInteger(input.durationDays, "Duration", 1, 30) }), pace: cleanPace(input.pace), notes: input.notes === undefined ? "" : cleanString(input.notes, "Notes", false) }, "agent"));
        bindings.setMessage(`Agent created ${trip.name} with ${trip.durationDays} day${trip.durationDays === 1 ? "" : "s"}.`);
        return { data: { trip: tripData(trip), visible: true } };
      }),
    },
    {
      name: "update_trip",
      title: "Update a trip",
      description: "Patch only the requested trip fields: name, destination, duration, paired exact dates, pace, or notes. Adding dates preserves existing day IDs and stops.",
      inputSchema: { type: "object", properties: { tripId: sharedTripIdProperty(), name: { type: "string" }, destination: { type: "string" }, durationDays: { type: "integer", minimum: 1, maximum: 30 }, startDate: { type: "string" }, endDate: { type: "string" }, pace: { type: "string", enum: ["relaxed", "balanced", "packed"] }, notes: { type: "string" } }, additionalProperties: false },
      annotations: { readOnlyHint: false, untrustedContentHint: true },
      execute: async (input: ToolInput) => executeTool(bindings, "update the trip", () => {
        const active = controller();
        const trip = resolveTrip(active, input.tripId);
        const patch: Partial<CreateTripInput> = {};
        if (Object.hasOwn(input, "name")) patch.name = cleanString(input.name, "Name");
        if (Object.hasOwn(input, "destination")) patch.destination = cleanString(input.destination, "Destination");
        if (Object.hasOwn(input, "durationDays")) patch.durationDays = cleanInteger(input.durationDays, "Duration", 1, 30);
        const hasStart = Object.hasOwn(input, "startDate");
        const hasEnd = Object.hasOwn(input, "endDate");
        if (hasStart !== hasEnd) fail("INVALID_INPUT", "Update startDate and endDate together.", { needsUserInput: true });
        if (hasStart) { patch.startDate = cleanString(input.startDate, "Start date"); patch.endDate = cleanString(input.endDate, "End date"); }
        if (patch.durationDays !== undefined) {
          if (hasStart) {
            const start = new Date(`${patch.startDate}T00:00:00.000Z`);
            const end = new Date(`${patch.endDate}T00:00:00.000Z`);
            const rangeDays = Math.round((end.getTime() - start.getTime()) / 86_400_000) + 1;
            if (!Number.isFinite(rangeDays) || rangeDays !== patch.durationDays) fail("INVALID_INPUT", "durationDays must match the paired exact date range.", { needsUserInput: true });
          } else if (trip.startDate && trip.endDate && patch.durationDays !== trip.durationDays) {
            fail("INVALID_INPUT", "This trip has exact dates, so durationDays is derived from them. Provide a new startDate and endDate together to change its duration.", { needsUserInput: true });
          }
        }
        if (Object.hasOwn(input, "pace")) patch.pace = cleanPace(input.pace, true);
        if (Object.hasOwn(input, "notes")) patch.notes = cleanString(input.notes, "Notes", false);
        if (!Object.keys(patch).length) fail("INVALID_INPUT", "Provide at least one trip field to update.", { needsUserInput: true });
        const updated = runDomain(() => active.updateTrip(trip.id, patch, "agent"));
        bindings.setMessage(`Agent updated ${updated.name}.`);
        return { data: { trip: tripData(updated), visible: true } };
      }),
    },
    {
      name: "open_trip",
      title: "Open a trip",
      description: "Open a trip for inspection. Navigation is not an undoable itinerary mutation.",
      inputSchema: { type: "object", properties: { tripId: { type: "string" } }, required: ["tripId"], additionalProperties: false },
      annotations: { readOnlyHint: false, untrustedContentHint: true },
      execute: async (input: ToolInput) => executeTool(bindings, "open the trip", () => {
        const active = controller();
        const trip = resolveTrip(active, input.tripId);
        active.openTrip(trip.id, "agent");
        bindings.setMessage(`Agent opened ${trip.name}.`);
        return { data: { trip: tripData(trip), visible: true } };
      }),
    },
    {
      name: "search_places",
      title: "Search places",
      description: "Search for a real place before adding it. Roam biases results to the trip destination unless worldwide is true. Returns a session-scoped placeRef required by add_place.",
      inputSchema: { type: "object", properties: { query: { type: "string" }, tripId: sharedTripIdProperty(), worldwide: { type: "boolean" } }, required: ["query"], additionalProperties: false },
      annotations: { readOnlyHint: true, untrustedContentHint: true },
      execute: async (input: ToolInput, { signal }) => executeTool(bindings, "search places", async () => {
        const query = cleanString(input.query, "Query");
        if (query.length < 3 || query.length > 120) fail("INVALID_INPUT", "Search query must contain between 3 and 120 characters.", { needsUserInput: true });
        const state = controller().getState();
        const trip = typeof input.tripId === "string" || state.activeTripId ? resolveTrip(controller(), input.tripId) : null;
        let results: PlaceCandidate[];
        try { results = await placeSearchProvider.search(query, signal, { destination: trip?.destination, worldwide: input.worldwide === true }); }
        catch (error) {
          if (error instanceof Error && error.name === "AbortError") throw error;
          fail("SEARCH_UNAVAILABLE", error instanceof Error ? error.message : "Place search is unavailable.", { retryable: true, suggestedTool: "search_places" });
        }
        const places = results.filter(isValidPlace).slice(0, 5).map((place) => ({ placeRef: cachePlace(place), name: place.name, address: place.displayName.slice(0, 180), lat: place.lat, lon: place.lon, category: place.category, type: place.type }));
        bindings.setMessage(`Agent found ${places.length} verified place result${places.length === 1 ? "" : "s"}.`);
        return { data: { provider: placeSearchProvider.id, query, destinationBias: input.worldwide === true ? null : trip?.destination ?? null, results: places }, meta: { capabilities: placeSearchProvider.capabilities } };
      }),
    },
    {
      name: "add_place",
      title: "Add a searched place",
      description: "Add a place using the exact placeRef from search_places. Never accept agent-supplied coordinates. Choose dayId and optional one-based targetPosition. If transit or other travel time must be feasible, ask for travelBufferMinutes instead of inventing it.",
      inputSchema: { type: "object", properties: { tripId: sharedTripIdProperty(), placeRef: { type: "string" }, dayId: { type: "string", description: "Required explicit TripDay ID. Ask the traveler when the intended day is unknown." }, targetPosition: { type: "integer", minimum: 1 }, startTime: { type: "string" }, durationMinutes: { type: "integer", minimum: 5, maximum: 1440 }, notes: { type: "string" }, cost: { type: "number", minimum: 0, maximum: 1000000 }, transport: { type: "string", enum: ["walk", "transit", "drive", "bike", "other"] }, travelBufferMinutes: { type: "integer", minimum: 1, maximum: 1440, description: "Required planning buffer when transport is transit or other and the traveler expects schedule feasibility." } }, required: ["placeRef", "dayId"], additionalProperties: false },
      annotations: { readOnlyHint: false, untrustedContentHint: true },
      execute: async (input: ToolInput) => executeTool(bindings, "add the place", () => {
        const active = controller();
        const trip = resolveTrip(active, input.tripId);
        const placeRef = cleanString(input.placeRef, "Place reference");
        const place = readCachedPlace(placeRef);
        if (!place) fail("SEARCH_RESULT_REQUIRED", "This placeRef is unavailable in the current page session. Call search_places again.", { suggestedTool: "search_places" });
        if (input.dayId === undefined) fail("INVALID_INPUT", "Choose an explicit dayId before adding this place. Do not default to Day 1.", { needsUserInput: true, suggestedTool: "summarize_trip" });
        const dayId = resolveDay(trip, input.dayId).id;
        const targetCount = trip.stops.filter((stop) => stop.dayId === dayId).length;
        const order = input.targetPosition === undefined ? targetCount + 1 : cleanInteger(input.targetPosition, "Target position", 1, targetCount + 1);
        const stop = runDomain(() => active.addStop(trip.id, { name: place.name, address: place.displayName, lat: place.lat, lon: place.lon, dayId, order, startTime: input.startTime === undefined ? undefined : cleanString(input.startTime, "Start time", false), durationMinutes: input.durationMinutes === undefined ? undefined : cleanInteger(input.durationMinutes, "Duration", 5, 1440), notes: input.notes === undefined ? "" : cleanString(input.notes, "Notes", false), cost: input.cost === undefined ? 0 : cleanNumber(input.cost, "Cost"), transport: cleanTransport(input.transport), travelBufferMinutes: input.travelBufferMinutes === undefined ? undefined : cleanInteger(input.travelBufferMinutes, "Travel buffer", 1, 1440), category: place.category || place.type || "place", placeRef: { id: place.id, provider: placeSearchProvider.id, resultId: placeRef }, explanation: `Verified result from ${placeSearchProvider.id}.` }, "agent"));
        bindings.setMessage(`Agent added ${stop.name} to Day ${trip.days.find((day) => day.id === dayId)?.dayIndex ?? 1}.`);
        return { data: { tripId: trip.id, stop: stopData(stop, active.getState().profile.currency), visible: true } };
      }),
    },
    {
      name: "update_stop",
      title: "Update a stop",
      description: "Patch one stop without moving it to another day. Locked place, time, day, and reservation fields are protected. Use move_stop for day/order changes. When transport is transit or other, ask for travelBufferMinutes before claiming schedule feasibility.",
      inputSchema: { type: "object", properties: { tripId: sharedTripIdProperty(), stopId: { type: "string" }, name: { type: "string" }, address: { type: "string" }, startTime: { type: "string" }, durationMinutes: { type: "integer", minimum: 5, maximum: 1440 }, notes: { type: "string" }, cost: { type: "number", minimum: 0, maximum: 1000000 }, transport: { type: "string", enum: ["walk", "transit", "drive", "bike", "other"] }, travelBufferMinutes: { type: "integer", minimum: 1, maximum: 1440, description: "Planned travel time from this stop to the next for transit or other modes." }, explanation: { type: "string" } }, required: ["stopId"], additionalProperties: false },
      annotations: { readOnlyHint: false, untrustedContentHint: true },
      execute: async (input: ToolInput) => executeTool(bindings, "update the stop", () => {
        const active = controller();
        const trip = resolveTrip(active, input.tripId);
        const stop = resolveStop(trip, input.stopId);
        const patch: UpdateStopInput = {};
        if (Object.hasOwn(input, "name")) patch.name = cleanString(input.name, "Name");
        if (Object.hasOwn(input, "address")) patch.address = cleanString(input.address, "Address", false);
        if (Object.hasOwn(input, "startTime")) patch.startTime = cleanString(input.startTime, "Start time", false);
        if (Object.hasOwn(input, "durationMinutes")) patch.durationMinutes = cleanInteger(input.durationMinutes, "Duration", 5, 1440);
        if (Object.hasOwn(input, "notes")) patch.notes = cleanString(input.notes, "Notes", false);
        if (Object.hasOwn(input, "cost")) patch.cost = cleanNumber(input.cost, "Cost");
        if (Object.hasOwn(input, "transport")) patch.transport = cleanTransport(input.transport);
        if (Object.hasOwn(input, "travelBufferMinutes")) patch.travelBufferMinutes = cleanInteger(input.travelBufferMinutes, "Travel buffer", 1, 1440);
        if (Object.hasOwn(input, "explanation")) patch.explanation = cleanString(input.explanation, "Explanation", false);
        if (!Object.keys(patch).length) fail("INVALID_INPUT", "Provide at least one stop field to update.", { needsUserInput: true });
        const updated = runDomain(() => active.updateStop(trip.id, stop.id, patch, "agent"));
        bindings.setMessage(`Agent updated ${updated.name}.`);
        return { data: { tripId: trip.id, stop: stopData(updated, active.getState().profile.currency), visible: true } };
      }),
    },
    {
      name: "move_stop",
      title: "Move a stop",
      description: "Move one stop using the same day-local semantics as the UI: targetDayId plus one-based targetPosition. Locked items and days are protected.",
      inputSchema: { type: "object", properties: { tripId: sharedTripIdProperty(), stopId: { type: "string" }, targetDayId: { anyOf: [{ type: "string" }, { type: "null" }], description: "Day ID, or null for Unscheduled." }, targetPosition: { type: "integer", minimum: 1 } }, required: ["stopId", "targetDayId", "targetPosition"], additionalProperties: false },
      annotations: { readOnlyHint: false, untrustedContentHint: true },
      execute: async (input: ToolInput) => executeTool(bindings, "move the stop", () => {
        const active = controller();
        const trip = resolveTrip(active, input.tripId);
        const stop = resolveStop(trip, input.stopId);
        const targetDayId = input.targetDayId === null ? null : resolveDay(trip, input.targetDayId).id;
        const maximum = trip.stops.filter((candidate) => candidate.id !== stop.id && candidate.dayId === targetDayId).length + 1;
        const targetPosition = cleanInteger(input.targetPosition, "Target position", 1, maximum);
        runDomain(() => active.moveStop(trip.id, stop.id, targetDayId, targetPosition, "agent"));
        bindings.setMessage(`Agent moved ${stop.name} to ${targetDayId ? `Day ${trip.days.find((day) => day.id === targetDayId)?.dayIndex}` : "Unscheduled"}, position ${targetPosition}.`);
        return { data: { tripId: trip.id, stopId: stop.id, targetDayId, targetPosition, routeRedrawn: true, feasibilityRecalculated: true, visible: true } };
      }),
    },
    {
      name: "refresh_routes",
      title: "Refresh real routes",
      description: "Refresh road-network distance and travel time after itinerary changes. Requires the traveler to enable online routing first. Transit and other modes use a traveler-provided buffer and open in Google Maps; Roam does not provide live transit, traffic, or GPS navigation.",
      inputSchema: { type: "object", properties: { tripId: sharedTripIdProperty(), scope: { type: "string", enum: ["day", "trip"] }, dayId: { type: "string", description: "Required when scope is day." }, force: { type: "boolean" } }, required: ["scope"], additionalProperties: false },
      annotations: { readOnlyHint: false, untrustedContentHint: true },
      execute: async (input: ToolInput, { signal }) => executeTool(bindings, "refresh routes", async () => {
        const active = controller();
        const trip = resolveTrip(active, input.tripId);
        if (input.scope !== "day" && input.scope !== "trip") fail("INVALID_INPUT", "Scope must be day or trip.", { needsUserInput: true });
        const day = input.scope === "day" ? resolveDay(trip, input.dayId) : null;
        const result = await active.refreshRoutes(trip.id, { ...(day ? { dayId: day.id } : {}), force: input.force === true, source: "agent", signal });
        const currentTrip = resolveTrip(active, trip.id);
        const routeLegs = result.routeLegs.map((leg) => ({ ...leg, navigationUrl: routeLegNavigationUrl(currentTrip, leg) }));
        bindings.setMessage(`Agent refreshed ${day ? `Day ${day.dayIndex}` : currentTrip.name} routes.`);
        return { data: { ...result, routeLegs, visible: true }, meta: { capabilities: travelCapabilities } };
      }),
    },
    {
      name: "summarize_trip",
      title: "Summarize a trip",
      description: "Read the whole trip or one day by dayId. Returns explicit stop order, route provenance, Google Maps handoff links, travel-aware conflicts, Day Health, locks, free time, and honest distance methods. Call refresh_routes before claiming road travel time after itinerary changes.",
      inputSchema: { type: "object", properties: { tripId: sharedTripIdProperty(), dayId: { type: "string" }, includeUnscheduled: { type: "boolean" }, limit: { type: "integer", minimum: 1, maximum: STOP_PAGE_LIMIT }, cursor: { type: "string" } }, additionalProperties: false },
      annotations: { readOnlyHint: true, untrustedContentHint: true },
      execute: async (input: ToolInput) => executeTool(bindings, "summarize the trip", () => {
        const active = controller();
        const trip = resolveTrip(active, input.tripId);
        const day = input.dayId === undefined ? null : resolveDay(trip, input.dayId);
        const profile = active.getState().profile;
        const scoped = day
          ? sortStops(trip.stops.filter((stop) => stop.dayId === day.id))
          : [
              ...trip.days.flatMap((tripDay) => sortStops(trip.stops.filter((stop) => stop.dayId === tripDay.id))),
              ...(input.includeUnscheduled === false ? [] : sortStops(trip.stops.filter((stop) => stop.dayId === null))),
            ];
        const signature = hashSignature(`${trip.id}:${trip.updatedAt}:${day?.id ?? "all"}:${scoped.map((stop) => `${stop.id}:${stop.updatedAt}:${stop.order}`).join("|")}`);
        const { offset, limit } = cleanPage(input, STOP_PAGE_LIMIT, 10, signature);
        const page = pageItems(scoped, offset, limit, signature);
        const summary = day ? summarizeDay(trip, day) : summarizeTrip(trip, profile.currency);
        const pageIds = new Set(page.items.map((stop) => stop.id));
        const conflicts = summary.conflicts.filter((conflict) => pageIds.has(conflict.firstStopId) || pageIds.has(conflict.secondStopId));
        const routeLegs = effectiveRouteLegs(trip).filter((leg) => (!day || leg.dayId === day.id) && (pageIds.has(leg.fromStopId) || pageIds.has(leg.toStopId))).map((leg) => ({ ...leg, navigationUrl: routeLegNavigationUrl(trip, leg) }));
        bindings.setMessage(`Agent reviewed ${day ? `Day ${day.dayIndex} of ` : ""}${trip.name}.`);
        return { data: { trip: tripData(trip), profile: { homeCity: profile.homeCity, currency: profile.currency, defaultPace: profile.pace }, scope: day ? { dayId: day.id, dayIndex: day.dayIndex, date: day.date ?? null } : { trip: true }, summary: { ...summary, conflicts, conflictScope: "returned_stop_page" }, stops: page.items.map((stop) => stopData(stop, profile.currency)), routeLegs, freeTimeBlocks: trip.freeTimeBlocks.filter((block) => !day || block.dayId === day.id), accommodation: day?.accommodation ?? null, ideasCount: trip.ideas.length, totalStops: page.total, nextCursor: page.nextCursor }, meta: { capabilities: travelCapabilities } };
      }),
    },
    {
      name: "preview_day_patch",
      title: "Preview a day patch",
      description: "Prepare a reviewable patch for one day without mutating the itinerary. Use for requests such as 'Make Day 2 more relaxed'. Locked items are preserved.",
      inputSchema: { type: "object", properties: { tripId: sharedTripIdProperty(), dayId: { type: "string" }, intent: { type: "string", enum: ["relaxed"] } }, required: ["dayId", "intent"], additionalProperties: false },
      annotations: { readOnlyHint: true, untrustedContentHint: true },
      execute: async (input: ToolInput) => executeTool(bindings, "preview the day change", () => {
        const active = controller();
        const trip = resolveTrip(active, input.tripId);
        const day = resolveDay(trip, input.dayId);
        if (input.intent !== "relaxed") fail("INVALID_INPUT", "Intent must be relaxed.");
        const preview = runDomain(() => active.proposeRelaxedDay(trip.id, day.id));
        bindings.setMessage(`Agent prepared a patch preview for Day ${day.dayIndex}; no itinerary change was applied.`);
        return { data: { preview, requiresUserReview: true, visible: true } };
      }),
    },
    {
      name: "undo_last_agent_change",
      title: "Undo the last agent change",
      description: "Undo only the immediately preceding agent mutation in this page session. Never undo a human change. Navigation and previews are not mutations.",
      inputSchema: { type: "object", properties: {}, additionalProperties: false },
      annotations: { readOnlyHint: false, untrustedContentHint: true },
      execute: async () => executeTool(bindings, "undo the last agent change", () => {
        const active = controller();
        const availability = active.getAgentUndoAvailability();
        if (availability === "none") fail("NO_UNDO_AVAILABLE", "No agent change is available to undo in this page session.");
        if (availability === "blocked") fail("AGENT_UNDO_BLOCKED", "The latest mutation was made by the traveler, so the agent cannot undo it.", { needsUserInput: true });
        let undone: AgentUndoResult;
        try { undone = active.undoLastAgentChange(); }
        catch (error) { fail("AGENT_UNDO_BLOCKED", error instanceof Error ? error.message : "The agent cannot undo this change.", { needsUserInput: true }); }
        bindings.setMessage(`Agent undid: ${undone.action}.`);
        return { data: { undone, activeTripId: active.getState().activeTripId, visible: true } };
      }),
    },
  ];
}

export function registerWebMCP(bindings: WebMCPBindings): AbortController {
  const registration = new AbortController();
  const context = document.modelContext;
  if (!context) {
    bindings.setStatus("unavailable");
    bindings.setMessage("WebMCP is not exposed by this browser; manual planning remains available.");
    return registration;
  }
  bindings.setStatus("checking");
  const tools = buildWebMCPTools(bindings, { registrationSignal: registration.signal });
  void Promise.all(tools.map((tool) => context.registerTool(tool, { signal: registration.signal }))).then(() => {
    bindings.setStatus("ready");
    bindings.setMessage(`${tools.length} WebMCP planning tools are ready.`);
  }).catch((error: unknown) => {
    bindings.setStatus("error");
    bindings.setMessage(error instanceof Error ? error.message : "WebMCP registration failed.");
  });
  return registration;
}
